import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { useDispatch } from 'react-redux'
import Resizer from 'react-image-file-resizer'
import { dataURIToBlob } from 'utils/fileUtils'
import { notifyToast } from 'utils/responseUtils'
import SketchCanvas from 'components/SketchCanvas/SketchCanvas'

const MaskType = ({
  itemKey,
  value,
  maskValue,
  selectedModel,
  setSelectedModel,
  uploadFilesMask,
  isMobile,
  isSmall,
}) => {
  const { t } = useTranslation('common')

  const dispatch = useDispatch()
  const canvasRef = useRef()

  const [fontWidth, setFontWidth] = useState(50)
  const [actualImageDimensions, setActualImageDimensions] = useState({
    height: '350px',
    width: isMobile ? '300px' : '350px',
  })

  useEffect(() => {
    if (canvasRef?.current && maskValue) {
      const img = new Image()
      img.src = maskValue

      img.onload = () => {
        const ratio = getAspectRatio(img)
        setActualImageDimensions({
          height: isMobile ? 300 * ratio : 350 * ratio,
          width: isMobile ? '300px' : '350px',
        })
      }
    }
  }, [maskValue])

  function getAspectRatio(image) {
    const w = image.naturalWidth
    const h = image.naturalHeight

    let aspectRatio

    if (w > h) {
      aspectRatio = w / h
    } else {
      aspectRatio = h / w
    }

    return aspectRatio
  }

  const resizeFile = (file, width, height) =>
    new Promise((resolve) => {
      Resizer.imageFileResizer(
        file,
        width,
        height,
        'PNG',
        100,
        0,
        (uri) => {
          resolve(uri)
        },
        'blob',
        width - 0.5,
        height - 0.5
      )
    })

  const exportMask = (itemKey) => {
    canvasRef.current?.prepareMask('#FFF', async (paths) => {
      if (paths.length === 0) {
        return dispatch(notifyToast('error', t('warning.add-mask')))
      }
      const file = canvasRef.current.toDataURL({})
      const blobFile = dataURIToBlob(file)
      const resizedBlobFile = await resizeFile(blobFile, actualImageDimensions.width, actualImageDimensions.height)
      uploadFilesMask(resizedBlobFile, itemKey)
    })
  }

  return (
    <div>
      <div className={`flex flex-col justify-between items-center gap-4 ${!isSmall ? 'md:flex-row' : ''}`}>
        <div className="w-[70%]">
          <SketchCanvas
            lineWidth={fontWidth}
            image={maskValue}
            color={'#FFF01Fb0'}
            canvasRef={canvasRef}
            loading={false}
            imageDimensions={actualImageDimensions}
          />
        </div>
        <div
          className="editor-tool md:w-[163px] h-[163px] md:h-[360px] flex-wrap md:flex-nowrap flex md:flex-col items-center justify-between rounded bg-white px-4 py-3"
          style={{
            display: isSmall ? 'none' : 'flex',
            boxShadow:
              '0px 8px 15px 0px rgba(99, 112, 131, 0.08), 0px 4px 6px 0px rgba(99, 112, 131, 0.08), 0px 0.9px 0px rgba(99, 112, 131, 0.12)',
          }}
        >
          <p className="text-black text-sm font-bold leading-7 w-full text-center md:w-auto">{t('model.mask-tool')}</p>
          <div className="range-slide">
            <input
              className="line-60"
              type="range"
              min={1}
              max={100}
              onChange={(e) => setFontWidth(parseInt(e.target.value, 10))}
              step={1}
              value={fontWidth}
            />
          </div>
          <div className={'flex items-start gap-2.5 order-2'}>
            <div onClick={() => canvasRef?.current?.undo()}>
              <img src={'/assets/common/undo.svg'} alt="undo" />
            </div>
            <div onClick={() => canvasRef?.current?.redo()}>
              <img src={'/assets/common/redo.svg'} alt="redo" />
            </div>
          </div>
        </div>
      </div>

      <div style={{ marginBlock: 20 }}>
        <div className="flex items-center justify-center gap-3">
          <button
            onClick={() => {
              if (value) {
                let tempModel = structuredClone(selectedModel)
                tempModel.args[itemKey].value = null
                setSelectedModel(tempModel)
              }
              canvasRef.current.clear()
            }}
            className="border border-solid border-[#e4e7ec] bg-white text-sm font-medium leading-5 text-[#101828] flex justify-center items-center text-center px-3 py-2 gap-2 self-stretch rounded-full"
          >
            {t('common.reset')}
            <img src={'/assets/common/reset.svg'} alt="reset" className="w-4" />
          </button>

          <button
            className="bg-[#000] h-[41px] text-white text-sm font-medium text-center leading-5 flex justify-center items-center gap-3 py-2.5 px-3.5 rounded-[120px]"
            onClick={() => exportMask(itemKey)}
          >
            {t('common.confirm')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default MaskType
